import { Stack, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import OverviewSvg from '~/@ui/_sharedSvgs/ui/Overview.svg?react'
import CoinsSvg from '~/@ui/_sharedSvgs/ui/Coins.svg?react'
import { OverviewTab } from './OverviewTab/OverviewTab'
import { TransactionsTab } from './TransactionsTab/TransactionsTab'
import { WelcomeOverview } from './WelcomeOverview/WelcomeOverview'
import { OverviewButton } from '../../@ui/buttons/button-overview/OverviewButton'
import { TopNavigationOwner } from '../navigation-top/TopNavigationOwner'
import WarningPurpleSvg from '~/@ui/_sharedSvgs/ui/WarningPuprle.svg?react'
import { useCurrentUserData } from '~/@biz_account/useCurrentUserData/useCurrentUserData'
import { InfoCard } from '~/@ui/info-card/InfoCard'
import { COLOR_PALLETE } from '~/COLOR_PALLETE'
import PaperPenSvg from '~/@ui/_sharedSvgs/ui/PaperPen.svg?react'

const TAB_MAPPING = {
  overview: 0,
  transactions: 1,
} as const

const INDEX_TO_TAB = {
  0: 'overview',
  1: 'transactions',
} as const

type TabName = keyof typeof TAB_MAPPING

export const PortfolioPage = () => {
  const navigate = useNavigate()
  const { selectedTab } = useParams()
  const { user, reloadUser } = useCurrentUserData()

  const getTabFromUrl = (): number => {
    const tabParam = selectedTab as TabName | undefined
    return TAB_MAPPING[tabParam ?? 'overview'] ?? 0
  }

  const [tabValue, setTabValue] = useState(getTabFromUrl)

  const handleTabChange = (value: React.SetStateAction<number>) => {
    const newTabValue = typeof value === 'function' ? value(tabValue) : value
    setTabValue(newTabValue)
    const tabName = INDEX_TO_TAB[newTabValue as keyof typeof INDEX_TO_TAB]
    navigate(`/vlastnik/portfolio/${tabName}`, {
      replace: true,
      preventScrollReset: true,
    })
  }

  const getCurrentOnboardingRoute = () => {
    let onboardingStep = 'informacie-o-nehnutelnosti' // _orderAndEstateStarted: true
    let onboardingId = user?._ownerPensionOrder?.estateId

    if (user?._ownerOnboardingStatus._orderEstateInitialsAdded) {
      onboardingStep = 'detaily-o-nehnutelnosti'
      onboardingId = user?._ownerPensionOrder?.estateId
    }

    if (user?._ownerOnboardingStatus._orderEstateRoomsInfoAdded) {
      onboardingStep = 'mesacny-dochodok'
      onboardingId = user?._ownerPensionOrder?.estateId
    }

    if (user?._ownerOnboardingStatus._orderEstateRentCalculationAdded) {
      onboardingStep = 'potrebne-dokumenty'
      onboardingId = user?._ownerPensionOrder?.id
    }

    return `/vlastnik/dotaznik/${onboardingStep}/${onboardingId}`
  }

  const isOnboardingOngoing =
    user?._ownerOnboardingStatus._orderAndEstateStarted ||
    user?._ownerOnboardingStatus._orderEstateInitialsAdded ||
    user?._ownerOnboardingStatus._orderEstateRentCalculationAdded ||
    user?._ownerOnboardingStatus._orderEstateRoomsInfoAdded

  useEffect(() => {
    setTabValue(getTabFromUrl())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  return (
    <Stack className="w-full overflow-hidden bg-zBlack200">
      <TopNavigationOwner user={user} />

      <Stack className="w-full max-w-[768px] self-center">
        <Stack className="gap-8 p-6 pb-8">
          <WelcomeOverview
            firstName={user?._personalInformation?.firstName ?? ''}
          />
        </Stack>

        {isOnboardingOngoing && (
          <InfoCard
            icon={<PaperPenSvg />}
            titleColor={COLOR_PALLETE.zWhite200}
            textColor={COLOR_PALLETE.zGrey450}
            bgColor={COLOR_PALLETE.zBlack300}
            btnColor={COLOR_PALLETE.zWhite200}
            linkText="Pokračovať"
            className="mx-6 mb-8"
            headerText="Vaša žiadost je takmer hotová!"
            bodyText="Vo vašej žiadosti ešte chýbajú niektoré detaily. Prosíme, prezrite si ju a doplňte ich."
            onClick={() => navigate(getCurrentOnboardingRoute())}
          />
        )}

        {user?._ownerPensionOrder &&
          user?._ownerOnboardingStatus._noActiveOrder && (
            <InfoCard
              icon={<WarningPurpleSvg />}
              bgColor="#f0ecff"
              className="mx-6 mb-8"
              headerText="Vaša žiadosť sa overuje"
              bodyText="Overenie môže trvať 1–3 pracovné dni. O výsledku vás budeme informovať SMS správou."
            />
          )}

        <Stack
          className="pb-28 bg-zWhite100 rounded-t-3xl"
          sx={{ minHeight: 'calc(100vh - 201px)' }}>
          <Typography className="text-xl font-bold text-zBlack200 p-6 pb-[15px]">
            Môj účet
          </Typography>

          <Stack className="w-full flex-row justify-between px-6">
            <Stack className="flex-grow w-1/2">
              <OverviewButton
                onClick={() => handleTabChange(0)}
                isSelected={tabValue === 0}>
                <OverviewSvg />
                Prehľad
              </OverviewButton>
            </Stack>

            <Stack className="flex-grow w-1/2">
              <OverviewButton
                onClick={() => handleTabChange(1)}
                isSelected={tabValue === 1}>
                <CoinsSvg />
                Transakcie
              </OverviewButton>
            </Stack>
          </Stack>

          {tabValue === 0 && (
            <OverviewTab user={user} reloadUser={reloadUser} />
          )}
          {tabValue === 1 && <TransactionsTab />}
        </Stack>
      </Stack>
    </Stack>
  )
}
